<template>
  <b-modal
    id="modal-reisetermin-auswahl"
    v-model="showModal"
    :visible="showModal"
    :hide-header-close="isInitialLoad"
    :no-close-on-backdrop="isInitialLoad"
    :no-close-on-esc="isInitialLoad"
    centered
    size="md"
    :title="modalTitle"
    @hidden="onModalHidden"
  >
    <div class="d-flex flex-column justify-content-center align-items-center px-4">
      <Calculator :size="60" class="text-danger mb-6"></Calculator>

      <!-- Katalog Page: Jahr, Monat, Reisekürzel, Reiseterminkürzel -->
      <template v-if="page === 'katalog'">
        <!-- Jahr -->
        <div class="filter-field mb-6" :class="{ 'has-value': filterValues.jahr !== null }">
          <AppSelect
            v-model="filterValues.jahr"
            :options="verfuegbareJahre"
            label="Jahr"
            placeholder="Jahr"
            hide-search
          />
        </div>

        <!-- Monat -->
        <div class="filter-field mb-6" :class="{ 'has-value': filterValues.monat !== null }">
          <AppSelect
            v-model="filterValues.monat"
            :options="verfuegbareMonate"
            label="Monat"
            placeholder="Monat"
            hide-search
            clearable
          />
        </div>

        <!-- Reisekürzel -->
        <div class="filter-field mb-6" :class="{ 'has-value': filterValues.reisekuerzel !== null }">
          <AppSelect
            v-model="filterValues.reisekuerzel"
            :options="reisenOptions"
            label="Reisekürzel"
            placeholder="Reisekürzel"
            clearable
            no-results-text="Keine Reisekürzel gefunden."
          />
        </div>

        <!-- Reiseterminkürzel -->
        <div class="filter-field mb-6" :class="{ 'has-value': !!filterValues.reiseterminkuerzel }">
          <AppSelect
            v-model="filterValues.reiseterminkuerzel"
            :options="reiseterminkuerzelAnzeigeOptions"
            :loading="loadingReiseterminkuerzel"
            :internal-search="false"
            label="Reiseterminkürzel"
            placeholder="Reiseterminkürzel"
            clearable
            empty-text="Tippe um Reisetermine zu suchen..."
            no-results-text="Keine Reisetermine gefunden."
            @search-change="reiseterminSucheAnstossen"
            @open="reiseterminSucheAnstossen()"
          />
        </div>
      </template>

      <!-- FIT Page: FIT Kürzel, Vorgang -->
      <template v-else-if="page === 'fit'">
        <!-- FIT Kürzel -->
        <div class="filter-field mb-6" :class="{ 'has-value': filterValues.fitReiseKuerzel !== null }">
          <AppSelect
            v-model="filterValues.fitReiseKuerzel"
            :options="fitReiseKuerzelOptions"
            :loading="loadingFitReiseKuerzel"
            label="FIT Reisekürzel"
            placeholder="FIT Reisekürzel"
            clearable
            no-results-text="Keine FIT Reisekürzel gefunden."
          />
        </div>

        <!-- Tour ID -->
        <div class="filter-field mb-6" :class="{ 'has-value': filterValues.tourId !== null }">
          <AppSelect
            v-model="filterValues.tourId"
            :options="tourIdAnzeigeOptions"
            :loading="loadingTourId"
            :internal-search="false"
            :empty-text="tourIdLeerText"
            label="Tour ID"
            placeholder="Tour ID"
            clearable
            no-results-text="Keine Tour ID gefunden."
            @search-change="onSearchChangeTourId"
            @open="onOpenTourId"
          />
        </div>
      </template>
    </div>

    <template #modal-footer>
      <b-button class="dialog-btn-secondary" @click="resetValues">Zurücksetzen</b-button>
      <b-button class="dialog-btn" variant="primary" :disabled="!isFilterComplete" @click="anwenden">
        Anwenden
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import * as odataService from '@/core/common/services/odata.service';
import apiService from '@/core/common/services/api.service';

import { Calculator } from 'lucide-vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import subMonths from 'date-fns/subMonths';

export default {
  components: {
    Calculator,
    AppSelect,
  },
  name: 'RepruAuswahlModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isInitialLoad: {
      type: Boolean,
      default: true,
    },
    selectedReisetermin: {
      type: Object,
      default: null,
    },
    page: {
      type: String,
      default: 'katalog',
      validator: value => ['katalog', 'fit'].includes(value),
    },
    selectedIndividualreisen: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    if (this.visible) {
      this.setHeaderZIndex('1051');
    }
  },
  beforeDestroy() {
    this.setHeaderZIndex('');
  },
  data() {
    return {
      filterValues: {
        // Katalog Page
        jahr: new Date().getFullYear(),
        monat: null,
        reisekuerzel: null,
        reiseterminkuerzel: null,
        // FIT Page
        fitReiseKuerzel: null,
        tourId: null,
      },
      // Katalog Page
      monate: [
        { value: 1, text: 'Januar' },
        { value: 2, text: 'Februar' },
        { value: 3, text: 'März' },
        { value: 4, text: 'April' },
        { value: 5, text: 'Mai' },
        { value: 6, text: 'Juni' },
        { value: 7, text: 'Juli' },
        { value: 8, text: 'August' },
        { value: 9, text: 'September' },
        { value: 10, text: 'Oktober' },
        { value: 11, text: 'November' },
        { value: 12, text: 'Dezember' },
      ],
      // Die gefundenen Reiseterminkürzel, die im Dropdown zur Auswahl stehen
      reiseterminkuerzelOptions: [],
      loadingReiseterminkuerzel: false,
      // Timer für die verzögerte Suche: erst kurz nach dem letzten Tastendruck wird wirklich gesucht
      sucheVerzoegerungsTimer: null,
      // Fortlaufende Nummer der zuletzt gestarteten Suche
      // Kommt eine Antwort mit einer veralteten Nummer zurück, gehört sie zu einer alten Eingabe und wird ignoriert
      letzteSucheNummer: 0,
      // Mit diesem Controller lässt sich eine noch laufende Server-Anfrage abbrechen, sobald eine neue Suche startet
      laufendeSucheAbbrechen: null,
      filteredReisenOptions: [],
      filteredMonateValues: [],
      isFilterValuebearbeitet: false,
      // FIT Page
      alleIndividualreisen: [],
      fitReiseKuerzelOptions: [],
      loadingFitReiseKuerzel: false,
      tourIdOptions: [],
      // NEU: Eigener Loading-State für die Tour ID Suche (unabhängig vom FIT Kürzel laden)
      loadingTourId: false,
      // NEU: Timer-Referenz für den Debounce der Tour ID Suche (500ms Verzögerung)
      timeoutTourIdSearch: null,
    };
  },
  computed: {
    ...mapGetters(['getReisekuerzelOptions']),

    // Tow way binding, damit das modal aus anderen komponenten wieder sichtbar wird
    showModal: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },

    modalTitle() {
      return this.page === 'fit' ? 'Bitte wähle eine Reise aus' : 'Bitte wähle einen Reisetermin aus';
    },

    reiseterminkuerzelAnzeigeOptions() {
      return this.mitGewaehltemWert(this.reiseterminkuerzelOptions, this.filterValues.reiseterminkuerzel);
    },
    tourIdAnzeigeOptions() {
      return this.mitGewaehltemWert(this.tourIdOptions, this.filterValues.tourId);
    },
    tourIdLeerText() {
      return this.filterValues.fitReiseKuerzel
        ? 'Keine Tour IDs verfügbar.'
        : 'Tippe um Tour IDs zu suchen...';
    },

    verfuegbareMonate() {
      if (this.filteredMonateValues.length > 0) {
        return this.monate.filter(m => this.filteredMonateValues.includes(m.value));
      }
      return this.monate;
    },

    reisenOptions() {
      if (this.filteredReisenOptions.length > 0) {
        return this.filteredReisenOptions;
      }
      return this.getReisekuerzelOptions.map(r => r.reisekuerzel).sort();
    },

    verfuegbareJahre() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // -2 bis +2 Jahre vom aktuellen Jahr
      for (let i = -2; i <= 2; i++) {
        years.push(currentYear + i);
      }
      return years.sort((a, b) => b - a); // Absteigend sortiert
    },

    isFilterComplete() {
      if (this.page === 'fit') {
        return this.filterValues.fitReiseKuerzel !== null && this.filterValues.tourId !== null;
      }
      return (
        this.filterValues.jahr !== null &&
        this.filterValues.monat !== null &&
        this.filterValues.reisekuerzel !== null &&
        this.filterValues.reiseterminkuerzel !== null &&
        this.filterValues.reiseterminkuerzel !== ''
      );
    },
  },
  watch: {
    // Katalog Page Watcher
    'filterValues.jahr': {
      handler() {
        if (this.page === 'katalog' && !this.isFilterValuebearbeitet) {
          this.filterValues.reiseterminkuerzel = null;
          this.reiseterminkuerzelOptions = [];
          this.filteredReisenOptions = [];
          this.filteredMonateValues = [];
        }
      },
    },
    'filterValues.monat': {
      handler() {
        if (this.page === 'katalog' && !this.isFilterValuebearbeitet) {
          this.filterValues.reiseterminkuerzel = null;
          this.reiseterminSucheAnstossen();
        }
      },
    },
    'filterValues.reisekuerzel': {
      handler(newReisekuerzel) {
        if (this.page === 'katalog' && !this.isFilterValuebearbeitet) {
          const currentReiseterminkuerzel = this.filterValues.reiseterminkuerzel;
          if (currentReiseterminkuerzel && newReisekuerzel) {
            const reiseterminkuerzelPrefix = currentReiseterminkuerzel.split('_')[0];
            if (reiseterminkuerzelPrefix !== newReisekuerzel) {
              this.filterValues.reiseterminkuerzel = null;
            }
          } else if (!newReisekuerzel) {
            this.filterValues.reiseterminkuerzel = null;
          }
          this.reiseterminSucheAnstossen();
        }
      },
    },
    'filterValues.reiseterminkuerzel': {
      handler(newValue) {
        if (this.page === 'katalog' && newValue && !this.isFilterValuebearbeitet) {
          const parts = newValue.split('_');
          if (parts.length === 2) {
            const reisekuerzel = parts[0];
            const datePart = parts[1].split('-');
            if (datePart.length === 3) {
              const monat = parseInt(datePart[1]);

              //verhindert, dass die anderen watcher getriggert werden
              this.isFilterValuebearbeitet = true;
              this.filterValues.reisekuerzel = reisekuerzel;
              this.filterValues.monat = monat;
              this.$nextTick(() => {
                this.isFilterValuebearbeitet = false;
              });
            }
          }
        }
      },
    },
    selectedReisetermin: {
      handler(newValue) {
        if (this.page === 'katalog' && newValue?.reiseterminkuerzel) {
          this.setFilterValuesFromReisetermin(newValue.reiseterminkuerzel);
        }
      },
      immediate: true,
    },

    // FIT Page Watcher
    'filterValues.fitReiseKuerzel': {
      handler(newFitReiseKuerzel) {
        if (this.page === 'fit' && !this.isFilterValuebearbeitet) {
          if (!newFitReiseKuerzel) {
            this.filterValues.tourId = null;
            this.tourIdOptions = [];
          } else if (this.filterValues.tourId) {
            const vorhanden = this.alleIndividualreisen.some(
              reise => reise.tourId === this.filterValues.tourId && reise.reisekuerzel === newFitReiseKuerzel
            );
            if (!vorhanden) {
              this.filterValues.tourId = null;
              this.tourIdOptions = [];
            }
          }
        }
      },
    },
    'filterValues.tourId': {
      handler(newTourId) {
        if (this.page === 'fit' && newTourId && !this.isFilterValuebearbeitet) {
          // Finde das zugehörige FIT Kürzel
          const individualreise = this.alleIndividualreisen.find(reise => reise.tourId === newTourId);
          if (individualreise && individualreise.reisekuerzel !== this.filterValues.fitReiseKuerzel) {
            this.isFilterValuebearbeitet = true;
            this.filterValues.fitReiseKuerzel = individualreise.reisekuerzel;
            this.$nextTick(() => {
              this.isFilterValuebearbeitet = false;
            });
          }
        }
      },
    },
    visible: {
      handler(newValue) {
        this.setHeaderZIndex(newValue ? '1051' : '');
        if (newValue && this.page === 'fit' && this.alleIndividualreisen.length === 0) {
          this.loadFitReiseKuerzel();
        }
      },
      immediate: true,
    },
    selectedIndividualreisen: {
      handler(newValue) {
        if (this.page === 'fit' && newValue) {
          this.setFilterValuesFromIndividualreise(newValue);
        }
      },
      immediate: true,
    },
  },
  methods: {
    // damit der menü header auf dieser seite über das modal geholt wird, um auf die anderen seiten navigieren zu können
    // somit werden andere seiten nicht beeinflusst
    setHeaderZIndex(value) {
      const header = document.getElementById('kt_header');
      const headerMobile = document.getElementById('kt_header_mobile');
      if (header) header.style.zIndex = value;
      if (headerMobile) headerMobile.style.zIndex = value;
    },

    mitGewaehltemWert(optionen, wert) {
      if (wert === null || wert === undefined || optionen.includes(wert)) {
        return optionen;
      }
      return [...optionen, wert];
    },

    // Startet eine Suche nach Reiseterminkürzeln
    reiseterminSucheAnstossen(suchtext) {
      // Eine eventuell schon geplante (beim tippen aber die 500ms sind noch nicht um), aber noch nicht gestartete Suche verwerfen
      // Es zählt immer nur die zuletzt eingegebene Suche
      if (this.sucheVerzoegerungsTimer) {
        clearTimeout(this.sucheVerzoegerungsTimer);
        this.sucheVerzoegerungsTimer = null;
      }

      // Eine eventuell schon laufende Server-Anfrage abbrechen
      // So blockiert die alte Anfrage keine Verbindung und ihr Ergebnis trifft nicht mehr ein
      if (this.laufendeSucheAbbrechen) {
        this.laufendeSucheAbbrechen.abort();
        this.laufendeSucheAbbrechen = null;
      }

      const hatFilter = this.filterValues.monat || this.filterValues.reisekuerzel;
      if (!suchtext && !hatFilter) {
        this.reiseterminkuerzelOptions = [];
        this.filteredReisenOptions = [];
        this.filteredMonateValues = [];
        this.loadingReiseterminkuerzel = false;
        return;
      }

      this.sucheVerzoegerungsTimer = setTimeout(() => {
        this.reisetermineVomServerLaden(suchtext);
      }, 500);
    },

    // Holt die passenden Reiseterminkürzel vom Server und übergibt sie zur Anzeige
    reisetermineVomServerLaden(suchtext) {
      this.loadingReiseterminkuerzel = true;

      // Jede Suche bekommt eine eigene, fortlaufende Nummer
      // Damit lässt sich später erkennen, ob eine eintreffende Antwort noch aktuell ist
      const sucheNummer = ++this.letzteSucheNummer;

      // Über diesen Controller kann genau diese Anfrage später abgebrochen werden
      const abbruch = new AbortController();
      this.laufendeSucheAbbrechen = abbruch;

      const url = this.reiseterminSucheUrlBauen(suchtext);

      apiService
        .get(url, '', { signal: abbruch.signal })
        .then(res => {
          // Ist inzwischen eine neuere Suche gestartet, gehört diese Antwort zu einer
          // alten Eingabe und wird ignoriert
          if (sucheNummer !== this.letzteSucheNummer) {
            return;
          }
          const reisetermine = res.data?.result?.value || [];
          this.reiseterminErgebnisseUebernehmen(reisetermine);
        })
        .catch(error => {
          // Eine absichtlich abgebrochene Anfrage ist kein Fehler
          if (error.code === 'ERR_CANCELED') {
            return;
          }
          console.error('Fehler bei der Reiseterminkürzel-Suche:', error);
        })
        .finally(() => {
          if (sucheNummer === this.letzteSucheNummer) {
            this.loadingReiseterminkuerzel = false;
          }
        });
    },

    // Baut die OData-URL für die Reiseterminkürzel-Suche aus Suchtext und gesetzten Filtern zusammen
    reiseterminSucheUrlBauen(suchtext) {
      const jahr = this.filterValues.jahr || new Date().getFullYear();
      const filterParts = [
        `startdatum ge ${jahr}-01-01`,
        `startdatum lt ${jahr + 1}-01-01`,
        'hotelleistungen/any(h: h/isDeleted eq false and h/vorgang/leistungen/any(l: l/deletedAt eq null))',
      ];
      // Nur Kürzel, die mit dem eingegebenen Text beginnen
      if (suchtext) {
        filterParts.push(`startswith(reiseterminkuerzel,'${suchtext}')`);
      }
      // Nur Kürzel des gewählten Reisekürzels (z. B. "ALRAUS_...")
      if (this.filterValues.reisekuerzel) {
        filterParts.push(`startswith(reiseterminkuerzel,'${this.filterValues.reisekuerzel}_')`);
      }
      // Nur Kürzel des gewählten Monats (z. B. "-04-" für April)
      if (this.filterValues.monat) {
        const monatZweistellig = String(this.filterValues.monat).padStart(2, '0');
        filterParts.push(`contains(reiseterminkuerzel,'-${monatZweistellig}-')`);
      }
      return `Reisetermin?$filter=${filterParts.join(' and ')}&$select=reiseterminkuerzel`;
    },

    reiseterminErgebnisseUebernehmen(reisetermine) {
      this.reiseterminkuerzelOptions = reisetermine.map(rt => rt.reiseterminkuerzel).sort();

      // Aus jedem Kürzel den Teil vor dem '_' herauslösen (z. B. "ALRAUS_2026-04-26" -> "ALRAUS")
      const reisekuerzel = reisetermine.map(rt => rt.reiseterminkuerzel.split('_')[0]).filter(Boolean);
      this.filteredReisenOptions = [...new Set(reisekuerzel)].sort();

      // Aus jedem Kürzel den Monat herauslesen (z. B. "ALRAUS_2026-04-26" -> 4)
      const monate = reisetermine
        .map(rt => {
          const datumsteil = rt.reiseterminkuerzel.split('_')[1];
          return datumsteil ? parseInt(datumsteil.split('-')[1]) : null;
        })
        .filter(monat => monat !== null);
      this.filteredMonateValues = [...new Set(monate)].sort((a, b) => a - b);
    },

    // damit der reiseterminFilter wieder mit dem angezeigten Reisetermin befüllt wird
    setFilterValuesFromReisetermin(kuerzel) {
      // Format: ALRAUS_2026-04-26
      const parts = kuerzel.split('_');
      // ['ALRAUS', '2026-04-26']
      if (parts.length === 2) {
        //ALRAUS
        const reisekuerzel = parts[0];
        // ['2026', '04', '26']
        const datePart = parts[1].split('-');
        if (datePart.length === 3) {
          const jahr = parseInt(datePart[0]);
          const monat = parseInt(datePart[1]);

          this.isFilterValuebearbeitet = true;

          this.filterValues.jahr = jahr;
          this.filterValues.monat = monat;
          this.filterValues.reisekuerzel = reisekuerzel;
          this.filterValues.reiseterminkuerzel = kuerzel;
          // Das Kürzel muss manuell in die Options eingefügt werden
          this.reiseterminkuerzelOptions = [kuerzel];

          this.$nextTick(() => {
            this.isFilterValuebearbeitet = false;
          });
        }
      }
    },

    resetValues() {
      if (this.page === 'fit') {
        this.filterValues.fitReiseKuerzel = null;
        this.filterValues.tourId = null;
      } else {
        this.filterValues.jahr = new Date().getFullYear();
        this.filterValues.monat = null;
        this.filterValues.reisekuerzel = null;
        this.filterValues.reiseterminkuerzel = null;
        this.filteredReisenOptions = [];
        this.filteredMonateValues = [];
      }
    },

    onModalHidden() {
      this.$emit('hidden');
    },

    anwenden() {
      if (this.page === 'fit') {
        this.$emit('anwenden', this.filterValues.tourId);
      } else {
        this.$emit('anwenden', this.filterValues.reiseterminkuerzel);
      }
    },

    async loadFitReiseKuerzel() {
      this.loadingFitReiseKuerzel = true;
      try {
        const heuteMinusSechsMonate = subMonths(new Date(), 6);
        // Parallel laden: Individualreisen und Vorgänge die Leistungen haben
        const individualreisen = await odataService.getIndividualreise({
          select: ['reisekuerzel', 'vorgangId', 'tourId'],
          filter: {
            isDeleted: { eq: false },
            startdatum: { ge: heuteMinusSechsMonate },
            vorgang: {
              leistungen: {
                any: {
                  deletedAt: { eq: null },
                },
              },
            },
          },
        });

        const fitReiseKuerzel = individualreisen.data.map(individualreise => individualreise.reisekuerzel);
        const uniqueFitReiseKuerzel = [...new Set(fitReiseKuerzel)];
        this.fitReiseKuerzelOptions = uniqueFitReiseKuerzel.sort();

        this.alleIndividualreisen = individualreisen.data;
      } catch (error) {
        console.error('Error loading FIT Kürzel:', error);
      } finally {
        this.loadingFitReiseKuerzel = false;
      }
    },

    // Beim Öffnen des Tour ID Dropdowns: Wenn FIT Kürzel gesetzt, direkt alle passenden Tour IDs anzeigen
    onOpenTourId() {
      if (this.filterValues.fitReiseKuerzel && this.tourIdOptions.length === 0) {
        const matchingTourIds = this.alleIndividualreisen
          .filter(reise => reise.reisekuerzel === this.filterValues.fitReiseKuerzel && reise.tourId)
          .map(reise => reise.tourId);
        this.tourIdOptions = [...new Set(matchingTourIds)].sort();
      }
    },

    // OData-Filter zum laden der TourID
    onSearchChangeTourId(searchText) {
      if (!searchText || searchText.length === 0) {
        this.tourIdOptions = [];
        return;
      }
      if (this.timeoutTourIdSearch) {
        clearTimeout(this.timeoutTourIdSearch);
      }
      this.timeoutTourIdSearch = setTimeout(() => {
        this.loadingTourId = true;
        const heuteMinusSechsMonate = subMonths(new Date(), 6);
        const datumStr = heuteMinusSechsMonate.toISOString().split('T')[0];
        let filterParts = [
          `startswith(tourId,'${searchText}')`,
          'isDeleted eq false',
          `startdatum ge ${datumStr}`,
          'vorgang/leistungen/any(l: l/deletedAt eq null)',
        ];
        // Handhabung, wenn fitReiseKuerzel schon befüllt wurde
        if (this.filterValues.fitReiseKuerzel) {
          filterParts.push(`reisekuerzel eq '${this.filterValues.fitReiseKuerzel}'`);
        }
        apiService
          .get(`Individualreise?$filter=${filterParts.join(' and ')}&$select=tourId,reisekuerzel`)
          .then(res => {
            const individualreisen = res.data?.result?.value || [];
            // Ergebnisse in alleIndividualreisen ergänzen, damit der Watcher das FIT Kürzel setzen kann
            individualreisen.forEach(r => {
              if (!this.alleIndividualreisen.some(a => a.tourId === r.tourId)) {
                this.alleIndividualreisen.push(r);
              }
            });
            const tourIds = [...new Set(individualreisen.map(r => r.tourId))];
            this.tourIdOptions = tourIds.sort();
          })
          .catch(error => {
            console.error('Error searching Tour IDs:', error);
          })
          .finally(() => {
            this.loadingTourId = false;
          });
      }, 500);
    },

    setFilterValuesFromIndividualreise(individualreise) {
      if (individualreise.length > 0 && individualreise[0].reisekuerzel && individualreise[0].tourId) {
        this.isFilterValuebearbeitet = true;
        this.filterValues.fitReiseKuerzel = individualreise[0].reisekuerzel;
        this.filterValues.tourId = individualreise[0].tourId;
        this.tourIdOptions = [individualreise[0].tourId];
        this.$nextTick(() => {
          this.isFilterValuebearbeitet = false;
        });
      }
    },
  },
};
</script>

<style scoped>
.filter-field {
  width: 100%;
  --app-select-width: 100%;
  --app-select-border-color: #e4e6ef;
  --app-select-placeholder-color: var(--text-body);
}

.filter-field.has-value,
.filter-field ::v-deep .app-select--open {
  --app-select-border-color: var(--primary);
}
</style>
